import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ClientsTable } from "../../../clients/clientsTable";
import { useClients } from "../../../clients/hooks/useClients";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { invalidateContactsAndClients } from "../../../../utils";

import { useAppSelector } from "../../../../redux/hooks/redux";
import { performSmartContactCleanup } from "../hooks/useContacts";

type Props = {
  phoneNumber: string;
  onDone: () => void;
  initialActiveTable?: "clients" | "prospects";
};

export function LinkClientPickerContent({ phoneNumber, onDone, initialActiveTable = "clients" }: Props) {
  const { linkContact } = useClients();
  const { showSnackbar } = useGlobalSnackbar();
  const queryClient = useQueryClient();
  const companyId = useAppSelector((state) => state.userState.company?.id);
  const [isLinking, setIsLinking] = useState(false);

  const contact = useMemo(
    () => ({
      uid: "",
      name: "",
      lastName: "",
      email: "",
      notes: "",
      phoneNumbers: [{ number: phoneNumber, label: "primary", isPrimary: true }],
    }),
    [phoneNumber],
  );

  const handleLinkContact = async (client: any) => {
    if (isLinking) return;

    setIsLinking(true);

    try {
      const res = await linkContact(client, contact);
      await performSmartContactCleanup(phoneNumber, companyId, queryClient);
      await invalidateContactsAndClients(queryClient);

      if (res?.message) {
        showSnackbar(res.message, "info");
      } else {
        showSnackbar("Phone number linked to client successfully", "success");
      }

      onDone();
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to link phone number", "error");
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <ClientsTable
      disableInnerChilds
      disableRecordClickable
      contact={contact}
      onLinkContact={handleLinkContact}
      initialActiveTable={initialActiveTable}
    />
  );
}
